import { useCallback } from 'react'
import { Alert } from 'react-native'
import Purchases from 'react-native-purchases'
import { useRelayEnvironment } from 'react-relay'

import { Toast, ToastTitle, useToast } from '@/components'
import { IS_WEB, PAYWALL_RESULT, refreshSparkBalance, RevenueCatUI, supabase } from '@/lib'

import { useAuth } from './useAuth'

// Default error surface so a missing offering or a thrown failure is NEVER a
// silent no-op, even when a caller (e.g. the header pill) passes no onError.
const defaultOnError = (message: string) => Alert.alert('Purchase', message)

type PaywallOffering = 'sparks' | 'plus'

/**
 * Presents a hosted RevenueCat paywall by offering id. Neither the `sparks` nor
 * the `plus` offering is the current offering, so both are read from
 * offerings.all and passed explicitly. On a successful purchase/restore it
 * repaints the Spark balance (a Spark purchase is also reconciled server-side
 * first) and shows a success toast. On web there is no native purchase SDK, so
 * it shows a "mobile app only" warning toast instead of running the paywall.
 *
 * @param onError - surfaces a user-facing message when the offering is missing or
 *   the flow throws. Falls back to a native Alert when omitted.
 */
export const usePaywall = (
  onError?: (message: string) => void
): {
  presentSparksPaywall: () => Promise<void>
  presentPlusPaywall: () => Promise<void>
} => {
  const { session } = useAuth()
  const environment = useRelayEnvironment()
  const toast = useToast()

  const present = useCallback(
    async (offeringId: PaywallOffering) => {
      if (IS_WEB) {
        // Purchases go through the native RevenueCat SDK, which does not exist on
        // web — but the CTAs (header pill, "Get More Sparks") are still tappable
        // here, so surface a message instead of a silent no-op. Use a toast, not
        // defaultOnError's Alert.alert (React Native Web does not render Alert), so
        // callers that pass no onError still get a visible warning.
        toast.show({
          placement: 'top',
          render: () => (
            <Toast
              action='warning'
              variant='solid'
            >
              <ToastTitle>Purchases are only available in the Dateability mobile app.</ToastTitle>
            </Toast>
          )
        })

        return
      }

      const surfaceError = onError ?? defaultOnError

      try {
        const { all } = await Purchases.getOfferings()
        const offering = all[offeringId]

        // `sparks` has no sensible fallback (packs live only in that offering);
        // `plus` falls back to the current offering, which is still a D+ paywall.
        if (!offering && offeringId === 'sparks') {
          surfaceError('Sparks are unavailable right now. Please try again later.')

          return
        }

        const result = offering ? await RevenueCatUI.presentPaywall({ offering }) : await RevenueCatUI.presentPaywall()

        if (result === PAYWALL_RESULT.PURCHASED || result === PAYWALL_RESULT.RESTORED) {
          // A Spark purchase settles on-device before the webhook lands, so
          // reconcile it on demand. A `plus` subscription grants Sparks via the
          // is_plus flip, which the customer-info listener's revenuecat_sync_user
          // call reconciles -- so only Spark purchases reconcile here.
          // supabase.functions.invoke never throws on an HTTP/network failure --
          // it resolves { data, error } -- so check the returned error.
          if (offeringId === 'sparks' && session?.user?.id) {
            // The function derives the user from the JWT that supabase-js attaches
            // automatically, so no userId is sent in the body.
            const { error: reconcileError } = await supabase.functions.invoke<string>('revenuecat_reconcile_sparks', {
              method: 'POST'
            })

            if (reconcileError) {
              console.warn('[sparks] post-purchase reconcile failed; webhook will backstop the credit', reconcileError)
            }
          }

          await refreshSparkBalance(environment)

          toast.show({
            placement: 'top',
            render: () => (
              <Toast
                action='success'
                variant='solid'
              >
                <ToastTitle>{`You're all set, thank you!`}</ToastTitle>
              </Toast>
            )
          })
        } else if (result === PAYWALL_RESULT.ERROR) {
          surfaceError('Something went wrong with the purchase. Please try again.')
        }
      } catch (error) {
        surfaceError(error instanceof Error ? error.message : 'Something went wrong opening the store.')
      }
    },
    [environment, onError, session?.user?.id, toast]
  )

  const presentSparksPaywall = useCallback(() => present('sparks'), [present])
  const presentPlusPaywall = useCallback(() => present('plus'), [present])

  return { presentSparksPaywall, presentPlusPaywall }
}
